/**
 * An invite opened from its link (`/join/<code>`): which team and role it leads to, and joining
 * once signed in (the sign-in card shows below until then).
 */
import { Match, Show, Switch, createSignal, type JSX } from "solid-js";
import { usePages } from "./context";

/** The invite card. */
export function JoinCard(): JSX.Element {
  const { accounts, server, go } = usePages();
  const signedIn = () => accounts.sessions().some((s) => s.server === server);
  const pending = () => accounts.pending();
  /** Set while joining (the invite is used up before the team shows). */
  const [joining, setJoining] = createSignal(false);

  /** Joins, then shows the team. */
  const join = async () => {
    setJoining(true);
    const team = await accounts.accept();
    setJoining(false);
    if (team) go({ page: "team", team: team.id });
  };

  return (
    <section class="pages-card join" id="join">
      <Switch>
        <Match when={pending()?.info}>
          {(info) => (
            <>
              <h2>
                Join <b>{info().team}</b>
              </h2>
              <p id="join-what">
                You are invited to the team <b>{info().team}</b> as <b>{info().role}</b>: you will see its spaces{info().role === "viewer" ? "" : " and work in them"}.
              </p>
              <Show when={signedIn()} fallback={<p class="muted">Sign in, or make an account, to join.</p>}>
                <span class="btnrow">
                  <button type="button" class="btn primary" id="join-accept" onClick={() => void join()}>
                    Join {info().team}
                  </button>
                  <button
                    type="button"
                    class="btn"
                    id="join-later"
                    onClick={() => {
                      accounts.dismiss();
                      go({ page: "home" });
                    }}
                  >
                    Not now
                  </button>
                </span>
              </Show>
            </>
          )}
        </Match>
        <Match when={joining()}>
          <p class="muted">Joining…</p>
        </Match>
        <Match when={pending() && !pending()?.gone}>
          <p class="muted">Looking at the invite…</p>
        </Match>
        <Match when={true}>
          <h2>This invite is not here any more</h2>
          <p class="muted">It was used or withdrawn. Ask an owner of the team for a new link.</p>
        </Match>
      </Switch>
    </section>
  );
}
